import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

import { dispatchStopLocations, dispatchStops, formatDispatchStopsTooltip } from './format';
import type { DispatchStopsSource } from './format';

const NS = 'operations';

/**
 * Pickup address of a request, shared by every dispatch surface. A one-stop
 * request renders exactly what the plain `location` cell used to render; a
 * multi-stop trip shows stop 1 plus how many more follow and exposes a clear
 * action for opening the full ordered list.
 */
export const DispatchStopsSummary = ({ row }: { row: DispatchStopsSource }) => {
  const { t } = useTranslation(NS);
  const stops = dispatchStops(row);
  const locations = dispatchStopLocations(row);

  if (locations.length < 2) return <>{locations[0] ?? '—'}</>;

  return (
    <div className="space-y-1.5">
      <span className="block break-words" title={formatDispatchStopsTooltip(row)}>
        {t('efs.operations.dispatch.stops.summary', {
          defaultValue: '{{first}} 他{{rest}}件',
          first: locations[0],
          rest: locations.length - 1,
        })}
      </span>

      <Dialog>
        <DialogTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="h-8">
            <MapPin aria-hidden="true" />
            {t('efs.operations.dispatch.stops.view_all', {
              defaultValue: '場所を{{count}}件表示',
              count: locations.length,
            })}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="border-b px-5 pb-4 pt-5 pr-12">
            <DialogTitle>
              {t('efs.operations.dispatch.stops.dialog_title', {
                defaultValue: '引取場所一覧',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('efs.operations.dispatch.stops.dialog_description', {
                defaultValue: 'この依頼に登録された引取場所を順番に表示します。',
              })}
            </DialogDescription>
          </DialogHeader>

          <ol className="grid max-h-[60vh] gap-3 overflow-y-auto px-5 py-1">
            {stops.map((stop) => (
              <li key={`${stop.stop_seq}-${stop.location}`} className="rounded-md border p-3">
                <p className="text-xs font-medium text-muted-foreground">
                  {t('efs.operations.dispatch.fields.stop_seq', {
                    defaultValue: '引取場所 {{seq}}',
                    seq: stop.stop_seq,
                  })}
                </p>
                <p
                  className="mt-1 whitespace-pre-wrap break-words text-sm font-medium"
                  data-testid="dispatch-stop-location"
                >
                  {stop.location}
                </p>
                {stop.notes ? (
                  <div className="mt-3 border-t pt-2 text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {t('efs.operations.dispatch.preview.stop_notes_label', {
                        defaultValue: '場所メモ',
                      })}
                      :{' '}
                    </span>
                    <span className="whitespace-pre-wrap break-words">{stop.notes}</span>
                  </div>
                ) : null}
              </li>
            ))}
          </ol>

          <DialogFooter className="border-t px-5 py-4">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                {t('efs.operations.dispatch.stops.close', { defaultValue: '閉じる' })}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DispatchStopsSummary;
